// Dok 40 – Rutin: Byggarbetsmiljösamordnare under utförande (Bas-U).
// Bas-U:s motsvarighet till Dok 01. Generisk rutin som projektanpassas.

export interface BasuSectionDef {
  key: string;
  title: string;
  hint: string;
  /** Förifylld standardtext som användaren kan justera */
  template: string;
}

export const BASU_ROUTINE_SECTIONS: BasuSectionDef[] = [
  {
    key: "syfte",
    title: "Syfte",
    hint: "Varför rutinen finns och vilka regler den utgår från.",
    template:
      "Rutinen beskriver arbetssättet för byggarbetsmiljösamordnare under utförande (Bas-U) enligt arbetsmiljölagen 3 kap. 7 b §. Den utgår från AFS 2023:3 kapitel 4 och kompletteras med krav ur AFS 2023:13, särskilt kapitel 5 om byggnads- och anläggningsarbeten. Syftet är att tydliggöra uppgifter, ansvarsfördelning och systematik för arbetsmiljösamordningen under produktionsskedet.",
  },
  {
    key: "omfattning",
    title: "Omfattning",
    hint: "Vilka skeden och arbeten rutinen gäller för.",
    template:
      "Rutinen gäller från förberedande arbete inför etablering, genom produktionsplanering och utförande, fram till avslut och överlämning. Är uppdraget handläggare åt en annan juridisk person som är utsedd Bas-U gäller den juridiska personens rutiner.",
  },
  {
    key: "ansvar",
    title: "Ansvar och handläggare",
    hint: "Vem som är huvudhandläggare och vad ansvaret innebär.",
    template:
      "Handläggare för Bas-U ska följa och projektanpassa denna rutin. Vid flera handläggare utses en huvudhandläggare för samordning och dokumentation.\n\nHuvudhandläggare: [namn och kontaktuppgifter]\nÖvriga handläggare: [namn och kontaktuppgifter]\n\nNär det står att Bas-U ska säkerställa kontroll av en uppgift innebär det att Bas-U ansvarar för att uppgiften blir utförd och dokumenterad på rätt sätt – inte att handläggaren själv måste ha den tekniska kompetensen eller utföra arbetet praktiskt.",
  },
  {
    key: "overtagande",
    title: "Överlämning av tidigare arbete",
    hint: "Vad som tas emot från Bas-P och byggherren.",
    template:
      "Vid uppdragsstart tas arbetsmiljöplanen, risksammanställningarna, arbetsområdesutredningen och kvarvarande frågor emot från Bas-P (Dok 20). Kvarstående punkter förs in i checklistan (Dok 41) med ansvarig och tidpunkt. Brister i underlaget meddelas byggherren skriftligt.",
  },
  {
    key: "produktionsplanering",
    title: "Produktionsplanering inför etablering",
    hint: "Arbete innan produktionen startar.",
    template:
      "Innan etablering granskas och uppdateras arbetsmiljöplanen, etableringen planeras med APD-plan (Dok 26), skyddsverksamheten fastställs (Dok 42), platsnärvaro och kontaktperson kommer överens med byggherren (Dok 43) och skydds- och ordningsreglerna anpassas (Dok 15).",
  },
  {
    key: "amp",
    title: "Granskning och uppdatering av arbetsmiljöplan",
    hint: "Hur AMP hålls aktuell under bygget.",
    template:
      "Arbetsmiljöplanen granskas vid övertagandet och uppdateras löpande när arbetsmetoder, etapper eller risker ändras, samt efter tillbud och olyckor. Varje ändring versionshanteras och kommuniceras till entreprenörerna via kvittens.",
  },
  {
    key: "samordning",
    title: "Samordning av entreprenörer och pågående verksamhet",
    hint: "Hur samordningen bedrivs praktiskt.",
    template:
      "Samordningen sker genom arbetsmiljö- och samordningsmöten, daglig styrning, skyddsronder och granskning av arbetsberedningar. Gemensamma skyddsanordningar, tidplan och etapper stäms av så att arbeten inte skapar risk för varandra. Pågående verksamhet och tredje man hanteras i särskild ordning.",
  },
  {
    key: "arbetsberedning",
    title: "Kontroll av arbetsberedningar och klartecken",
    hint: "Bas-U granskar – entreprenören äger metoden.",
    template:
      "Entreprenören ska lämna arbetsberedning innan riskfyllda moment påbörjas. Bas-U granskar att risker är identifierade, bedömda och hanterade, att kompetens och behörigheter finns och att behovet av samordning är beskrivet. Klartecken för start ges skriftligt (Dok 24b). Bas-U tar inte över entreprenörens ansvar för vald arbetsmetod.",
  },
  {
    key: "tillfalliga",
    title: "Tillfälliga och samverkande konstruktioner",
    hint: "Kompetent person och kontroll.",
    template:
      "Ställningar, formställningar, stämp, spont och andra tillfälliga eller samverkande konstruktioner förs in i registret (Dok 44). Bas-U kontrollerar att det finns utsedd kompetent person och konstruktionshandlingar. Saknas resurs meddelas byggherren skriftligt enligt AFS 2023:3 kap. 2 § 17.",
  },
  {
    key: "uppfoljning",
    title: "Uppföljning, tillbud och avvikelser",
    hint: "Hur brister fångas upp och stängs.",
    template:
      "Avvikelser fångas upp genom skyddsronder (Dok 27), platsbesök och uppföljning av utförandet (Dok 21). Tillbud och olyckor rapporteras och utreds (Dok 28). Vid allvarliga brister avbryts arbetet. Upprepade regelbrott hanteras enligt trappan för disciplinära åtgärder (Dok 46).",
  },
  {
    key: "avslut",
    title: "Avslut och överlämning",
    hint: "Vad som lämnas vidare när bygget är klart.",
    template:
      "Vid avslut genomförs den överlämnande skyddsronden, kvarstående bruksrisker sammanställs och arbetsmiljödokumentationen lämnas över till byggherren (Dok 22 och Dok 34). Avvikelser som inte hunnit stängas listas med ansvarig mottagare.",
  },
  {
    key: "egenkontroll",
    title: "Egenkontroll av rutinen",
    hint: "Hur rutinen följs upp och förbättras.",
    template:
      "Rutinen stäms av mot checklistan (Dok 41) inför varje nytt skede och vid uppdragets slut. Avvikelser mot rutinen dokumenteras och används för förbättring i kommande uppdrag.",
  },
];

export const BASU_ROUTINE_STATUS_LABELS: Record<string, string> = {
  utkast: "Utkast",
  granskning: "Under granskning",
  faststalld: "Fastställd",
};
